import type {
    CabinetAiCall,
    CabinetCall,
    CabinetDocument,
    CabinetEmail,
    CabinetLetter,
    CabinetMail,
    CabinetReceivedFax,
    CabinetSentFax,
    CabinetTranscription,
    FileCabinetApp,
    FileCabinetCapabilities,
    FileCabinetDataSource,
    FileCabinetFileUrls,
    FileCabinetListKey,
    FileCabinetListRequest,
    FileCabinetPage,
    FileCabinetRecord,
    FileCabinetRecordByList,
    FileCabinetRecordByView,
    FileCabinetViewKey,
} from './file-cabinet.types';
import { FileCabinetRequestError } from './file-cabinet.types';
import { fileCabinetWireFilters } from './file-cabinet.query';

/**
 * The query a generated list method takes: the page and sort, plus every filter — already that
 * endpoint's parameter names (`filterCreated`, `filterOcr`, …). The provider and support request
 * types have the same shape; each clients file casts it to its own.
 */
export type FileCabinetQuery = Record<string, unknown>;

/**
 * One app's lists, records and file links — `/provider/…` or `/support/…`. Kept apart from the network
 * layer (`file-cabinet.provider-clients.ts`, `file-cabinet.support-clients.ts`) so this file and its
 * tests never touch it. Every support model carries its provider twin's fields, so one source reads both.
 */
export interface FileCabinetClients {
    listDocuments(query: FileCabinetQuery): Promise<FileCabinetPage<CabinetDocument>>;
    listMail(query: FileCabinetQuery): Promise<FileCabinetPage<CabinetMail>>;
    listLetters(query: FileCabinetQuery): Promise<FileCabinetPage<CabinetLetter>>;
    listReceivedFaxes(query: FileCabinetQuery): Promise<FileCabinetPage<CabinetReceivedFax>>;
    listSentFaxes(query: FileCabinetQuery): Promise<FileCabinetPage<CabinetSentFax>>;
    listSentEmails(query: FileCabinetQuery): Promise<FileCabinetPage<CabinetEmail>>;
    /** Every method — inbound, outbound, other — split by `filterMethod`. */
    listCalls(query: FileCabinetQuery): Promise<FileCabinetPage<CabinetCall>>;
    /** `GET /phone/ai`. */
    listAiCalls(query: FileCabinetQuery): Promise<FileCabinetPage<CabinetAiCall>>;

    getDocument(id: string): Promise<CabinetDocument>;
    getMail(id: string): Promise<CabinetMail>;
    getLetter(id: string): Promise<CabinetLetter>;
    getReceivedFax(id: string): Promise<CabinetReceivedFax>;
    getSentFax(id: string): Promise<CabinetSentFax>;
    getSentEmail(id: string): Promise<CabinetEmail>;
    getCall(id: string): Promise<CabinetCall>;
    /** `GET /phone/ai/{id}` — keyed by `aiPhoneId`. */
    getAiCall(id: string): Promise<CabinetAiCall>;

    /** `GET /document/{id}/transcription` — a call recording's transcript. */
    transcription(documentId: string): Promise<CabinetTranscription | undefined>;
    /** `GET /document/{id}/download` — what both apps already preview and download with. */
    documentUrl(documentId: string): Promise<string | undefined>;
    /**
     * `GET /document/{id}/embed` — the same file served inline. The download link carries
     * `Content-Disposition: attachment`, so a new tab on it downloads instead of opening (measured 2026-09-23).
     */
    documentEmbedUrl(documentId: string): Promise<string | undefined>;
    /** `GET /letter/{id}/view` — the letter as printed. */
    letterUrl(letterId: string): Promise<string | undefined>;
    /** `GET /fax/send/{id}/view` — the fax as sent. */
    sentFaxUrl(faxId: string): Promise<string | undefined>;
}

function query(list: FileCabinetListKey, request: FileCabinetListRequest, capabilities: FileCabinetCapabilities): FileCabinetQuery {
    const filters = fileCabinetWireFilters(list, request.filters);
    // Active documents only, as the old Documents page lists them — unless the Active filter chose, or the
    // documents are asked for by id (a source document, a link): one asked for by id is shown whatever its state.
    if (list === 'docs' && capabilities.inactiveDocuments && !filters.filterActive && !filters.filterId) {
        filters.filterActive = ['EQ:1'];
    }
    return {
        limit: request.limit,
        // The provider app omits offset on the first page; the API reads absent as 0 either way.
        offset: request.offset || undefined,
        sort: request.sort ? [request.sort] : undefined,
        order: request.order ? [request.order] : undefined,
        ...filters,
    };
}

/** The File Cabinet on the API — the provider app's or the support app's, by the clients given. */
export class ApiFileCabinetDataSource implements FileCabinetDataSource {
    public readonly capabilities: FileCabinetCapabilities;

    constructor(
        public readonly app: FileCabinetApp,
        /** The values the document-type filter offers — this app's display enum. */
        public readonly documentTypes: string[],
        private readonly clients: FileCabinetClients,
    ) {
        this.capabilities = { fullTextSearch: true, rangeFilters: true, inactiveDocuments: app === 'support' };
    }

    public async list<L extends FileCabinetListKey>(
        list: L,
        request: FileCabinetListRequest,
    ): Promise<FileCabinetPage<FileCabinetRecordByList[L]>> {
        const page = await this.listOf(list, query(list, request, this.capabilities));
        return page as FileCabinetPage<FileCabinetRecordByList[L]>;
    }

    public async get<V extends FileCabinetViewKey>(view: V, id: string): Promise<FileCabinetRecordByView[V] | undefined> {
        let record: FileCabinetRecord | undefined;
        try {
            record = await this.getOf(view, id);
        } catch (error) {
            // No record with that key — a stale or mistyped link — is an answer, not a failure.
            if (error instanceof FileCabinetRequestError && error.notFound) {
                return undefined;
            }
            throw error;
        }
        if (!record) {
            return undefined;
        }
        // A call opened from a link under the wrong table — an inbound call as `call.out` — is not that
        // table's record: no drawer, rather than one that contradicts the table behind it.
        if (view === 'call.in' || view === 'call.out' || view === 'call.other') {
            const method = (record as CabinetCall).method;
            const fixed = fileCabinetWireFilters(view, {}).filterMethod?.[0] ?? '';
            if (!method || !fixed.split(',').includes(`EQ:${method}`)) {
                return undefined;
            }
        }
        return record as FileCabinetRecordByView[V];
    }

    public async fileUrls(view: FileCabinetViewKey, record: FileCabinetRecord): Promise<FileCabinetFileUrls | undefined> {
        switch (view) {
            case 'docs':
                return this.documentUrls((record as CabinetDocument).id);
            case 'mail.out': {
                // The letter as printed — not the document it mailed, which is `letter.document`.
                const letter = record as CabinetLetter;
                return letter.id && !letter.cancelled ? same(await this.clients.letterUrl(letter.id)) : undefined;
            }
            case 'fax.out': {
                const id = (record as CabinetSentFax).id;
                return id ? same(await this.clients.sentFaxUrl(id)) : undefined;
            }
            case 'mail.in':
            case 'fax.in': {
                const carrier = record as CabinetMail | CabinetReceivedFax;
                return this.documentUrls(carrier.document?.id ?? carrier.documentId);
            }
            case 'email.out':
                // The attachment, when the email had one.
                return this.documentUrls((record as CabinetEmail).document?.id);
            case 'call.in':
            case 'call.out':
            case 'call.other':
                // The recording.
                return this.documentUrls((record as CabinetCall).document?.id);
            case 'call.ai':
            default:
                // An AI call has no document in the API.
                return undefined;
        }
    }

    public transcription(documentId: string): Promise<CabinetTranscription | undefined> {
        return this.clients.transcription(documentId);
    }

    /**
     * Both links, each allowed to fail alone. Either serves the preview — pdf.js fetches the file
     * whatever its disposition — so one working link is enough. Only when neither exists is the record
     * fileless; a failure is never reported as "no file" (NS-3).
     */
    private async documentUrls(documentId: string | undefined): Promise<FileCabinetFileUrls | undefined> {
        if (!documentId) {
            return undefined;
        }
        const [download, view] = await Promise.allSettled([
            this.clients.documentUrl(documentId),
            this.clients.documentEmbedUrl(documentId),
        ]);
        const downloadUrl = download.status === 'fulfilled' ? download.value : undefined;
        const viewUrl = view.status === 'fulfilled' ? view.value : undefined;
        if (downloadUrl || viewUrl) {
            return { download: downloadUrl || (viewUrl as string), view: viewUrl || (downloadUrl as string) };
        }
        if (download.status === 'rejected') {
            throw download.reason;
        }
        if (view.status === 'rejected') {
            throw view.reason;
        }
        return undefined;
    }

    private listOf(list: FileCabinetListKey, request: FileCabinetQuery): Promise<FileCabinetPage<FileCabinetRecord>> {
        switch (list) {
            case 'docs':
                return this.clients.listDocuments(request);
            case 'mail.in':
                return this.clients.listMail(request);
            case 'mail.out':
                return this.clients.listLetters(request);
            case 'fax.in':
                return this.clients.listReceivedFaxes(request);
            case 'fax.out':
                return this.clients.listSentFaxes(request);
            case 'email.out':
                return this.clients.listSentEmails(request);
            case 'call.in':
            case 'call.out':
            case 'call.other':
            case 'call.all':
                return this.clients.listCalls(request);
            case 'call.ai':
                return this.clients.listAiCalls(request);
            default: {
                const unknown: never = list;
                throw new Error(`No list for the File Cabinet "${String(unknown)}"`);
            }
        }
    }

    private getOf(view: FileCabinetViewKey, id: string): Promise<FileCabinetRecord | undefined> {
        switch (view) {
            case 'docs':
                return this.clients.getDocument(id);
            case 'mail.in':
                return this.clients.getMail(id);
            case 'mail.out':
                return this.clients.getLetter(id);
            case 'fax.in':
                return this.clients.getReceivedFax(id);
            case 'fax.out':
                return this.clients.getSentFax(id);
            case 'email.out':
                return this.clients.getSentEmail(id);
            case 'call.in':
            case 'call.out':
            case 'call.other':
                return this.clients.getCall(id);
            case 'call.ai':
                return this.clients.getAiCall(id);
            default: {
                const unknown: never = view;
                throw new Error(`No record for the File Cabinet view "${String(unknown)}"`);
            }
        }
    }
}

/** The letter and sent-fax `/view` links have no separate inline variant; one URL serves both buttons. */
function same(url: string | undefined): FileCabinetFileUrls | undefined {
    return url ? { download: url, view: url } : undefined;
}
